import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCheckoutFormProps, StripeComponentProps } from "../types";
import { useCopies } from "shared/hooks/useCopies";
import React from "react";
import ButtonType from "shared/enums/ButtonTypes";
import Button from "shared/components/button";
import { errorToast } from "shared/utils/toastNotification";
import Variant from "shared/enums/Variant";
import { useRefetchPurchaseOrderDetails } from "../dataloader";
import ApiPurchaseOrderRefreshRequest from "shared/types/ApiPurchaseOrderRefreshRequest";
import { RedirectQueryParam } from "shared/enums/Redirect";
import { STRIPE_PAYMENT_SUCCESS_LIST, StripePaymentStatus } from "../enum";
import ApiValidateInitTokenResponse from "shared/types/ApiValidateInitTokenResponse";
import { isRegPortal } from "shared/utils/config";
import Icon from "assets/icons";
import IFrameQueryParam from "shared/enums/IFrameQueryParam";
import { Base64Util } from "shared/utils/base64";
import CancelPaymentButton from "./cancelButton";
import {
  AnalyticsContextEntityTypeEnum,
  AnalyticsEventNameEnum,
  AnalyticsScreenNameEnum
} from "shared/enums/AnalyticsEnums";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";

export default function StripeComponent({
  service,
  stripeReturnUrl,
  registrationParticipantId,
  purchaseOrderId,
  onAmountMismatch,
  amountObj,
  logPurchaseError,
  setPaymentNonce,
  invoicePayerId,
  onPurchaseOrderVerificationFailure,
  backToOfflineRequestedClickHandler
}: StripeComponentProps) {
  return (
    <div className="payment_container payment_container--stripePayment">
      {service.stripePromise && service.apiPaymentInitToken?.initToken && (
        <Elements
          key={`${service.apiPaymentInitToken?.initToken}`}
          options={{ clientSecret: service.apiPaymentInitToken?.initToken }}
          stripe={service.stripePromise}>
          <CheckoutForm
            setPayButtonDisabled={service.setPayButtonDisabled}
            setDisplayLoader={service.setDisplayLoader}
            isPayButtonDisabled={service.isPayButtonDisabled}
            stripeReturnUrl={stripeReturnUrl}
            registrationParticipantId={registrationParticipantId}
            purchaseOrderId={purchaseOrderId}
            amountObj={amountObj}
            getNewInitToken={service.getNewInitToken}
            onAmountMismatch={onAmountMismatch}
            initToken={service.apiPaymentInitToken?.initToken}
            logPurchaseError={logPurchaseError}
            setPaymentNonce={setPaymentNonce}
            invoicePayerId={invoicePayerId}
            onPurchaseOrderVerificationFailure={onPurchaseOrderVerificationFailure}
            backToOfflineRequestedClickHandler={backToOfflineRequestedClickHandler}
          />
        </Elements>
      )}
    </div>
  );
}

function CheckoutForm({
  setPayButtonDisabled,
  setDisplayLoader,
  isPayButtonDisabled,
  stripeReturnUrl,
  registrationParticipantId,
  purchaseOrderId,
  amountObj,
  getNewInitToken,
  onAmountMismatch,
  initToken,
  logPurchaseError,
  setPaymentNonce,
  invoicePayerId,
  onPurchaseOrderVerificationFailure,
  backToOfflineRequestedClickHandler
}: StripeCheckoutFormProps) {
  const copies = useCopies();
  const stripe = useStripe();
  const elements = useElements();
  const { mutateAsync: refetchPurchaseOrderDetails } = useRefetchPurchaseOrderDetails(
    onPurchaseOrderVerificationFailure
  );

  React.useEffect(() => {
    // this is done so that Purchase button gets enabled only after stripe payment is fully loaded
    const element = elements?.getElement("payment");
    element?.on("ready", () => {
      setPayButtonDisabled(false);
    });
  }, [elements, setPayButtonDisabled]);

  const handleSubmit = React.useCallback(
    async e => {
      e.preventDefault();

      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      setDisplayLoader(true);
      setPayButtonDisabled(true);

      try {
        const refetchRequestObj: ApiPurchaseOrderRefreshRequest = {
          purchaseOrderId,
          invoicePayerId: invoicePayerId && isRegPortal ? invoicePayerId : undefined,
          registrationParticipantId: invoicePayerId && isRegPortal ? undefined : registrationParticipantId
        };
        let refetchedDetails: ApiValidateInitTokenResponse | undefined;
        try {
          refetchedDetails = await refetchPurchaseOrderDetails(refetchRequestObj);
        } catch (e) {
          return;
        }

        // check if amount has changed when user was on payment screen due to price break
        // it may happen that initToken is changed which may happen if previous initToken/payment_intent is marked as cancelled by Stripe
        if (
          amountObj.value !== refetchedDetails?.purchaseOrder.amount.value ||
          initToken !== refetchedDetails?.paymentInitToken.initToken
        ) {
          // in both the cases we need to reinitialize stripe component and update amount on UI as well
          onAmountMismatch?.(refetchedDetails?.purchaseOrder);
          errorToast(
            amountObj.value !== refetchedDetails?.purchaseOrder.amount.value
              ? copies.get("purchase.order.amount.mismatch.stripe")
              : copies.get("stripe.payment.secret.changed")
          );
          getNewInitToken();
          return;
        }

        // to be used for storing error object returned by stripe
        let errorObj;
        let paymentIntentObj;

        if (stripeReturnUrl) {
          const updatedStripeUrl = new URL(stripeReturnUrl);
          const pt_url = updatedStripeUrl.searchParams.get(IFrameQueryParam.PERSONATECH_REG_URL);
          if (pt_url) {
            // if url is opened in iframe then add amount obj in encoded pt_url which will be opened in iframe again
            const decodedPtUrl = new URL(atob(pt_url));
            decodedPtUrl.searchParams.append(RedirectQueryParam.AMOUNT_OBJECT, JSON.stringify(amountObj));
            const encodedPtUrl = Base64Util.encode(decodedPtUrl.toString()) as string;
            updatedStripeUrl.searchParams.set(IFrameQueryParam.PERSONATECH_REG_URL, encodedPtUrl);
          } else {
            // if not in iframe then append amount object in url directly
            updatedStripeUrl.searchParams.append(RedirectQueryParam.AMOUNT_OBJECT, JSON.stringify(amountObj));
          }

          // used in Reg
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              // redirect URL with latest payment amount
              return_url: updatedStripeUrl.toString()
            },
            redirect: "if_required"
          });

          errorObj = error;
          paymentIntentObj = paymentIntent;
        } else {
          // used in Console and MyExp
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: "if_required"
          });

          errorObj = error;
          paymentIntentObj = paymentIntent;
        }

        if (
          paymentIntentObj?.status &&
          STRIPE_PAYMENT_SUCCESS_LIST.includes(paymentIntentObj.status as StripePaymentStatus)
        ) {
          setPaymentNonce(paymentIntentObj.id);
          return;
        }

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (errorObj?.type === "validation_error") {
        } else if (errorObj?.type === "card_error") {
          errorToast(errorObj.message);
          // log error / card decline code in analytics
          logPurchaseError?.(
            AnalyticsEventNameEnum.ERROR,
            {
              screenType: AnalyticsScreenNameEnum.PAYMENT,
              contextEntityType: AnalyticsContextEntityTypeEnum.PURCHASE_ORDER,
              contextEntityId: purchaseOrderId,
              contextEntityName: errorObj.decline_code
            },
            AnalyticsDatastoreType.MIXPANEL_INTERNAL
          );
        } else if (errorObj) {
          errorToast(errorObj?.message ? errorObj.message : copies.get("payment.transaction.failed"));
          // log other error messages in analytics e.g. 3dsecure cancelled or something else
          logPurchaseError?.(
            AnalyticsEventNameEnum.ERROR,
            {
              screenType: AnalyticsScreenNameEnum.PAYMENT,
              contextEntityType: AnalyticsContextEntityTypeEnum.PURCHASE_ORDER,
              contextEntityId: purchaseOrderId,
              contextEntityName: errorObj?.message ? errorObj.message : copies.get("payment.transaction.failed")
            },
            AnalyticsDatastoreType.MIXPANEL_INTERNAL
          );
        }
      } catch (e) {
        errorToast(copies.get("stripe.confirm.payment.error"));
        logPurchaseError?.(
          AnalyticsEventNameEnum.ERROR,
          {
            screenType: AnalyticsScreenNameEnum.PAYMENT,
            contextEntityType: AnalyticsContextEntityTypeEnum.PURCHASE_ORDER,
            contextEntityId: purchaseOrderId,
            contextEntityName: "handleSubmit.catch.error"
          },
          AnalyticsDatastoreType.MIXPANEL_INTERNAL
        );
      } finally {
        setDisplayLoader(false);
        setPayButtonDisabled(false);
      }
    },
    [
      amountObj,
      copies,
      elements,
      getNewInitToken,
      initToken,
      logPurchaseError,
      onAmountMismatch,
      invoicePayerId,
      purchaseOrderId,
      refetchPurchaseOrderDetails,
      registrationParticipantId,
      setDisplayLoader,
      setPayButtonDisabled,
      setPaymentNonce,
      stripe,
      stripeReturnUrl
    ]
  );

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      <div className="paymentWrapper_btnWrapper">
        <Button
          icon={<Icon.ShoppingBag />}
          label={copies.get("purchase")}
          variant={Variant.PRIMARY}
          type={ButtonType.SUBMIT}
          disabled={isPayButtonDisabled || !stripe || !elements}
        />
        <CancelPaymentButton clickHandler={backToOfflineRequestedClickHandler} />
      </div>
    </form>
  );
}
